import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ely_token";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = user
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const applyToken = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common["Authorization"];
    }
    setToken(t);
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t) {
        setUser(false);
        return;
      }
      axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        setUser(data.user);
      } catch (e) {
        applyToken(null);
        setUser(false);
      }
    };
    bootstrap();
  }, [applyToken]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    applyToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    applyToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    applyToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
